import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Input, Link, Text, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { AuthWrapper, SwitchWorkspaceModal } from '../../components';
import { AuthContext, AuthRoute, useEmailField } from '../../features';
import { useBooleanState } from '../../hooks';
export const LoginPage = () => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { getIDPConfiguration, login, error, redirectToDefaultPage, setError, ssoLogin } = useContext(AuthContext);
    const passwordRef = useRef(null);
    const { email, emailError, emailHasError, emailTouched, onEmailBlur, onEmailChange } = useEmailField();
    const [password, setPassword] = useState('');
    const [isPasswordMode, enablePasswordMode, disablePasswordMode] = useBooleanState(false);
    const [isSwitchWorkspaceModalOpen, openSwitchWorkspaceModal, closeSwitchWorkspaceModal] = useBooleanState(false);
    const [availableEntities, setAvailableEntities] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState();
    const isNextDisabled = isPasswordMode ? !password : !email || emailHasError;
    const isSSOAvailable = selectedEntity && selectedEntity.loginMethods.includes('sso');
    const showPassword = (isPasswordMode || !isSSOAvailable) && selectedEntity;
    const showSSO = !isPasswordMode && isSSOAvailable;
    const onPasswordChange = (event) => {
        setPassword(event.target.value);
        setError(undefined);
    };
    const onNext = async () => {
        if (!isNextDisabled) {
            if (showPassword) {
                await login(email, password, selectedEntity?.id);
                await redirectToDefaultPage();
            }
            else if (isSSOAvailable) {
                await ssoLogin(selectedEntity, email);
            }
            else {
                validateEmail();
            }
        }
    };
    const validateEmail = async () => {
        const ipdList = await getIDPConfiguration(email);
        if (ipdList.length === 0) {
            addNotification({
                title: translate('Invalid email'),
                subtitle: translate('The email you entered is not associated with any account. Please check the email or sign up for a new account.'),
                type: 'error',
            });
        }
        if (ipdList.length === 1) {
            setSelectedEntity(ipdList[0]);
        }
        else {
            setAvailableEntities(ipdList);
            openSwitchWorkspaceModal();
        }
    };
    const onSignUpClick = () => {
        const url = 'https://signup.medable.com/';
        window.open(url, '_blank', 'noopener,noreferrer');
    };
    const onSelectWorkspace = (entity) => {
        setSelectedEntity(entity);
        closeSwitchWorkspaceModal();
        if (!entity.loginMethods.includes('sso')) {
            passwordRef.current?.focus();
        }
    };
    return (_jsxs(AuthWrapper, { title: translate('Sign in to your account'), error: error, onSubmit: onNext, children: [_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(Input, { "data-testid": 'login-email-input', errorMessage: emailError, hasError: emailTouched && emailHasError, id: 'email', label: translate('Email'), onBlur: onEmailBlur, onChange: onEmailChange, required: true, hideRequiredIndicator: true, value: email, width: 'full', disabled: !!selectedEntity }), showPassword && (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'relative', children: [_jsx("div", { className: 'absolute right-0 top-0', children: _jsx(Link, { LinkComponent: RouterLink, to: AuthRoute.FORGOT_PASSWORD, children: translate('Forgot password?') }) }), _jsx(Input, { "data-testid": 'login-password-input', id: 'password', label: translate('Password'), onChange: onPasswordChange, ref: passwordRef, required: true, hideRequiredIndicator: true, type: 'password', value: password, width: 'full', autoFocus: true })] }), _jsx(Button, { dataTestId: 'login-button', label: translate('Sign in'), disabled: isNextDisabled })] })), (showSSO || !selectedEntity) && (_jsx(Button, { dataTestId: 'login-continue-button', label: showSSO ? translate('Sign in with SSO') : translate('Continue'), disabled: isNextDisabled })), isSSOAvailable && (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'flex items-center max-w-[27.5rem]', children: [_jsx("div", { className: 'border-b w-full' }), _jsx(Text, { size: 'sm', className: 'mx-4 text-secondary font-medium', children: translate('or') }), _jsx("div", { className: 'border-b w-full' })] }), showPassword ? (_jsx(Button, { type: 'button', label: translate('Sign in with SSO'), onClick: disablePasswordMode, variant: 'secondary' })) : (_jsx(Button, { type: 'button', dataTestId: 'login-with-password-button', label: translate('Sign in with password'), onClick: enablePasswordMode, variant: 'secondary' }))] })), _jsxs("div", { className: 'flex gap-1 text-sm self-center mt-10', children: [translate("Don't have an account?"), _jsx(Link, { to: '#', onClick: onSignUpClick, children: translate('Sign up') })] })] }), isSwitchWorkspaceModalOpen && availableEntities.length > 0 && (_jsx(SwitchWorkspaceModal, { entities: availableEntities, onSubmit: onSelectWorkspace, onClose: closeSwitchWorkspaceModal }))] }));
};
